import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { usePhasedAgentReplyWait } from '../../hooks/usePhasedAgentReplyWait';

export interface AgentReplyWaitIndicatorProps {
  /** When true, show the phased wait status (typically ChatContext `isTyping`). */
  active: boolean;
  /** Override the first-phase line (default: “Your guide is working…”). */
  initialMessage?: string;
  /** Override the second-phase line after ~10s. */
  extendedMessage?: string;
  /** Optional inline styles for Discovery / full-page layouts. */
  style?: React.CSSProperties;
  className?: string;
  'data-testid'?: string;
}

/**
 * Shared “agent is working” / “gathering context…” indicator for chat turns.
 */
export const AgentReplyWaitIndicator: React.FC<AgentReplyWaitIndicatorProps> = ({
  active,
  initialMessage,
  extendedMessage,
  style,
  className,
  'data-testid': testId = 'agent-reply-wait',
}) => {
  const { visible, message, extended } = usePhasedAgentReplyWait(active, {
    initialMessage,
    extendedMessage,
  });

  if (!visible) return null;

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        color: 'rgba(0,0,0,0.45)',
        fontSize: 13,
        ...style,
      }}
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-extended={extended ? 'true' : 'false'}
    >
      <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14 }} />} size="small" />
      <span>{message}</span>
    </div>
  );
};
